import { useCallback, useEffect, useMemo, useState } from "react";
import { toFixed } from "../utils/helpers/NumberHelper";

// NOTE: this file previously imported `toFixed2`, which does not exist in
// NumberHelper.js. The import has been corrected to `toFixed`. The function
// is not currently called anywhere in this hook, so it had no runtime effect.

/**
 * Manages a unit selector: tracks the selected unit and provides a converter
 * function from the selected unit to the standard (base) unit.
 *
 * When the unit changes, `transformValueHandler` is called with the ratio
 * (newUnit.value / prevUnit.value) so the caller can scale any displayed value
 * to keep it visually consistent while the internal representation stays in
 * base units.
 *
 * @param {((coefficient: number) => void) | null} transformValueHandler -
 *   Called with a scaling coefficient when the user switches units.
 * @param {{ value: number, label: string } | null} initUnit - Initially selected unit
 * @returns {{
 *   getValueInStandartUnits: (value: number) => number,
 *   unitLabel: string,
 *   unit: { value: number, label: string } | null,
 *   handleUnitChange: (event: React.ChangeEvent<HTMLInputElement>) => void
 * }}
 */
const useUnits = (transformValueHandler, initUnit) => {
  const [unit, setUnit] = useState(initUnit);
  useEffect(() => {
    setUnit(initUnit);
  }, [initUnit]);

  const { getValueInStandartUnits, unitLabel } = useMemo(() => {
    return {
      getValueInStandartUnits: (value) => value / unit?.value,
      unitLabel: unit?.label,
    };
  }, [unit]);

  const handleUnitChange = useCallback(
    (event) => {
      const unit = event.target.value;
      setUnit((prevUnit) => {
        transformValueHandler &&
          transformValueHandler(unit.value / prevUnit.value);
        return unit;
      });
    },
    [transformValueHandler]
  );
  return {
    getValueInStandartUnits,
    unitLabel,
    unit,
    handleUnitChange,
  };
};

/**
 * Combines a numeric value with a unit selector.
 *
 * Provides the raw display value (in the user-selected unit), the same value
 * converted to the base unit for calculations, a setter for the display value,
 * and the unit selector state + handler.
 *
 * When the user switches units, the display value is automatically rescaled so
 * the number shown stays consistent (e.g. switching from kg to g multiplies the
 * displayed number by 1000).
 *
 * @param {number | null} initValue - Initial display value
 * @param {{ value: number, label: string } | null} initUnit - Initially selected unit
 * @returns {[
 *   number,                                                    // value in base units
 *   number,                                                    // display value
 *   (value: number) => void,                                   // setValue
 *   string,                                                    // unit label
 *   { value: number, label: string } | null,                  // current unit object
 *   (event: React.ChangeEvent<HTMLInputElement>) => void       // handleUnitChange
 * ]}
 */
const useMeasureInUnits = (initValue, initUnit) => {
  const [value, setValue] = useState(initValue);
  const { getValueInStandartUnits, unitLabel, unit, handleUnitChange } =
    useUnits(
      (coefficient) => setValue((value) => value * coefficient),
      initUnit
    );

  const valueInStandartUnits = useMemo(
    () => getValueInStandartUnits(value),
    [getValueInStandartUnits, value]
  );

  return [
    valueInStandartUnits,
    value,
    setValue,
    unitLabel,
    unit,
    handleUnitChange,
  ];
};

export { useMeasureInUnits, useUnits };
